import React, { useEffect, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { logger } from '@/utils/logger';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Core state management
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  // Use a typed interface for profile data
  interface UserProfile {
    id: string;
    user_id: string;
    subscription_tier?: string;
    created_at?: string;
    updated_at?: string;
    [key: string]: unknown; // Allow for additional properties
  }
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Create a new profile if one doesn't exist
  // Memoize this function to avoid dependency issues in useEffect
  const createProfile = React.useCallback(async (userId: string) => {
    try {
      // Get the current user data at function call time to avoid stale closures
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const metadata = currentUser?.user_metadata || {};
      
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          subscription_tier: 'free'
        })
        .select()
        .single();
      
      if (error) {
        logger.error("Error creating profile", error instanceof Error ? error : undefined, { userId });
        return;
      }
      
      logger.info("Created new profile for user", { userId });
      setProfile(data);
    } catch (error) {
      logger.error("Error in createProfile", error instanceof Error ? error : undefined, { userId });
    }
  }, [/* No dependencies needed as we fetch fresh data */]);

  // Fetch user profile
  const fetchProfile = React.useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(); // Use maybeSingle instead of single to handle case when profile doesn't exist
      
      if (error) {
        logger.error("Error fetching profile", error instanceof Error ? error : undefined, { userId });
        return;
      }
      
      if (data) {
        setProfile(data);
      } else {
        logger.info("Profile not found, creating new profile", { userId });
        // Create a new profile if one doesn't exist
        await createProfile(userId);
      }
    } catch (error) {
      logger.error("Error in fetchProfile", error instanceof Error ? error : undefined, { userId });
    }
  }, [createProfile]);

  // Initialize auth state
  useEffect(() => {
    logger.debug("Setting up auth state listener", { component: 'AuthProvider' });
    
    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      logger.debug("Auth state change event", { event, userId: currentSession?.user?.id || null });
      
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (event === 'SIGNED_OUT') {
        logger.info("User signed out", { event });
        setProfile(null);
      }
    });
    
    // Check for existing session (after setting up listener)
    const checkSession = async () => {
      try {
        logger.debug("Checking for existing session");
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (currentSession?.user) {
          logger.info("Found existing session", { userId: currentSession.user.id });
          setSession(currentSession);
          setUser(currentSession.user);
          await fetchProfile(currentSession.user.id);
        } else {
          logger.debug("No existing session found");
        }
        
        setLoading(false);
      } catch (error) {
        logger.error("Error checking session", error instanceof Error ? error : undefined);
        setLoading(false);
      }
    };
    
    checkSession();
    
    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]); // Add fetchProfile as a dependency

  // Refresh user profile data
  const refreshProfile = async () => {
    if (!user) return;
    
    try {
      await fetchProfile(user.id);
    } catch (error) {
      logger.error("Error in refreshProfile", error instanceof Error ? error : undefined, { userId: user?.id });
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string, rememberMe?: boolean) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      // Profile will be fetched by the auth listener
      return data;
    } catch (error: unknown) {
      logger.error("Sign in error", error, { email });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string, fullName: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      logger.error("Sign up error", error instanceof Error ? error : undefined, { email });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      logger.error("Sign out error", error instanceof Error ? error : undefined);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    session,
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};